import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { configs } from '@/configs';
import { network } from '@/configs/network';
import type { InputExecuteSSO } from '@/libs/ssoWallet';
import { ActionSSO, ssoExecute } from '@/libs/ssoWallet';
import { useAuth } from '@/provider/UserProvider';
import { getMessageExecuteLockTokenWithCw20Token } from '@/utils/transactionMessage';
import { handleExecute } from '@/utils/wasm';

import { LOCK_CONTRACT_ADDRESS, NetworkKey, TOKEN_CONTRACT_ADDRESS } from '../constants';

const MESS_FINISH = 'Locked AIRI successfully!';

const useLockWithOraiChain = (callback: () => void) => {
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [finish, setFinish] = useState('');

  const checkGas = (gas: number) => {
    if (gas > 4000000 || gas < 400000) {
      toast.error('Gas number must be between 400,000 and 4,000,000');
    }
  };
  const convertAmount = (amount: string) => (parseFloat(amount) * 10 ** 6).toString();

  const handleExecuteWithOwallet = async (message: string, additionalInfor: object) => {
    setLoading(true);
    const res = await handleExecute(
      TOKEN_CONTRACT_ADDRESS[NetworkKey.Oraichain],
      auth?.user?.publicAddress,
      message,
      additionalInfor,
    );
    if (res?.tx_response?.txhash) {
      setLoading(false);
      setFinish(MESS_FINISH);
    }
  };

  const handleExecuteWithSSO = async (message: string) => {
    const input: InputExecuteSSO = {
      contractAddress: TOKEN_CONTRACT_ADDRESS[NetworkKey.Oraichain],
      chainId: network?.chainId || 'Oraichain',
      params: message,
    };
    setLoading(true);
    await ssoExecute(input, ActionSSO.lockToken);
  };

  const handleLockToken = async ({
    poolId,
    userId,
    amount,
    fees,
    gas,
  }: {
    poolId: number;
    userId: number;
    amount: string;
    fees: number;
    gas: number;
  }) => {
    setErr('');
    setFinish('');
    checkGas(gas);
    const message = getMessageExecuteLockTokenWithCw20Token({
      userId,
      poolId,
      amount: convertAmount(amount),
      contract: LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
    });
    const additionalInfor = {
      gas,
      fees,
      mode: 'BROADCAST_MODE_BLOCK',
    };

    try {
      if (auth.isOwallet) {
        await handleExecuteWithOwallet(message, additionalInfor);
      } else {
        await handleExecuteWithSSO(message);
      }
    } catch (error: any) {
      console.log('error lock token: ', error);
      setLoading(false);
      toast.error(error?.message || 'Error');
    }
  };

  // for sso
  useEffect(() => {
    if (typeof window !== 'undefined' && !auth.isOwallet) {
      window.addEventListener(
        'message',
        (event) => {
          if (event.origin !== configs.ssoUrl) return;
          const data = JSON.parse(event?.data || '');

          if (data.action !== ActionSSO.lockToken) return;
          if (data.status !== 'success') {
            console.log('err lock airi: ', data.response);
            setErr(data.response);
          } else {
            setLoading(false);
            setFinish(MESS_FINISH);
          }
        },
        false,
      );
    }
    return window.removeEventListener('message', () => {});
  }, []);

  useEffect(() => {
    if (err) {
      toast.error(err);
    }
  }, [err]);

  useEffect(() => {
    if (finish) {
      toast.success(finish);
      callback();
    }
  }, [finish]);

  return { handleLockToken, loading };
};

export default useLockWithOraiChain;
